import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as insuranceApi from './insurance.api';

import type {
  CreateInsurancePricingTierRequest,
  CreateInsuranceRequest,
  InsuranceApprovalRequest,
  InsuranceListQuery,
  UpdateInsurancePricingTierRequest,
  UpdateInsuranceRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: InsuranceListQuery) => ['insurance', 'list', query] as const,
  detail: (id: string) => ['insurance', 'detail', id] as const,
  tiers: (insuranceId: string) => ['insurance', 'tiers', insuranceId] as const,
};

export const useInsuranceList = (query: InsuranceListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => insuranceApi.listInsurance(query),
    staleTime: 30_000,
  });

export const useInsuranceDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => insuranceApi.getInsurance(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

export const useCreateInsurance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInsuranceRequest) => insuranceApi.createInsurance(payload),
    onSuccess: (insurance) => {
      qc.invalidateQueries({ queryKey: ['insurance', 'list'] });
      toast.success(`Added ${insurance.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add insurance'),
  });
};

export const useUpdateInsurance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateInsuranceRequest }) =>
      insuranceApi.updateInsurance(id, payload),
    onSuccess: (insurance) => {
      qc.invalidateQueries({ queryKey: ['insurance', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(insurance.id) });
      toast.success('Insurance updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update insurance'),
  });
};

export const useApproveInsurance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: InsuranceApprovalRequest }) =>
      insuranceApi.approveInsurance(id, payload),
    onSuccess: (insurance, vars) => {
      qc.invalidateQueries({ queryKey: ['insurance', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(insurance.id) });
      toast.success(
        `${vars.payload.tier === 'admin' ? 'Admin' : 'Moderator'} ${vars.payload.approve ? 'approved' : 'approval revoked'}`,
      );
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to change approval'),
  });
};

export const useDeleteInsurance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => insuranceApi.deleteInsurance(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['insurance', 'list'] });
      toast.success('Insurance deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete insurance'),
  });
};

export const useBulkDeleteInsurance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => insuranceApi.bulkDeleteInsurance(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['insurance'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete insurance'),
  });
};

// ---------- Pricing tier hooks ----------

export const useInsurancePricingTiers = (insuranceId: string | null | undefined) =>
  useQuery({
    queryKey: QK.tiers(insuranceId ?? ''),
    queryFn: () => insuranceApi.listInsurancePricingTiers(insuranceId as string),
    staleTime: 30_000,
    enabled: Boolean(insuranceId),
  });

export const useCreateInsurancePricingTier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      insuranceId,
      payload,
    }: {
      insuranceId: string;
      payload: CreateInsurancePricingTierRequest;
    }) => insuranceApi.createInsurancePricingTier(insuranceId, payload),
    onSuccess: (tier, vars) => {
      qc.invalidateQueries({ queryKey: QK.tiers(vars.insuranceId) });
      qc.invalidateQueries({ queryKey: QK.detail(vars.insuranceId) });
      toast.success(`Added tier ${tier.tierName}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add tier'),
  });
};

export const useUpdateInsurancePricingTier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      insuranceId,
      tierId,
      payload,
    }: {
      insuranceId: string;
      tierId: string;
      payload: UpdateInsurancePricingTierRequest;
    }) => insuranceApi.updateInsurancePricingTier(insuranceId, tierId, payload),
    onSuccess: (_tier, vars) => {
      qc.invalidateQueries({ queryKey: QK.tiers(vars.insuranceId) });
      qc.invalidateQueries({ queryKey: QK.detail(vars.insuranceId) });
      toast.success('Tier updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update tier'),
  });
};

export const useDeleteInsurancePricingTier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ insuranceId, tierId }: { insuranceId: string; tierId: string }) =>
      insuranceApi.deleteInsurancePricingTier(insuranceId, tierId),
    onSuccess: (_v, vars) => {
      qc.invalidateQueries({ queryKey: QK.tiers(vars.insuranceId) });
      qc.invalidateQueries({ queryKey: QK.detail(vars.insuranceId) });
      toast.success('Tier deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete tier'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
